import React, { useState, useRef, useEffect } from 'react';
import '../../styles/AddTransporterModal.css';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../config/apiConfig.js';
import Swal from 'sweetalert2';


const CreatableSelect = ({ value, onChange, options, onAddOption, placeholder, onKeyDown }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (isOpen && activeIndex >= 0 && dropdownRef.current) {
            const activeEl = dropdownRef.current.children[activeIndex];
            if (activeEl) {
                activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }
    }, [activeIndex, isOpen]);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const newVal = e.target.value;
        setSearchTerm(newVal);
        onChange(newVal);
        setIsOpen(true);
        setActiveIndex(0);
    };

    const handleOptionClick = (opt) => {
        onChange(opt);
        setSearchTerm(opt);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const handleBlur = (e) => {
        // Use a small delay to allow clicking on options in the dropdown
        setTimeout(() => {
            if (containerRef.current && !containerRef.current.contains(document.activeElement)) {
                setIsOpen(false);
            }
        }, 150);
    };

    const handleAddClick = () => {
        onAddOption(searchTerm);
        setIsOpen(false);
        setActiveIndex(-1);
    };

    const filteredOptions = (options || []).filter(opt =>
        opt && opt.toString().toLowerCase().includes((searchTerm || '').toLowerCase())
    );

    const showAddOption = onAddOption && searchTerm && !filteredOptions.some(opt => opt && opt.toString().toLowerCase() === searchTerm.toLowerCase());

    const handleInternalKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'Enter') {
                if (typeof onKeyDown === 'function') {
                    onKeyDown(e);
                }
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                setActiveIndex(0);
            }
            return;
        }

        const totalOptions = filteredOptions.length + (showAddOption ? 1 : 0);

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev + 1) % totalOptions);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev - 1 + totalOptions) % totalOptions);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
                handleOptionClick(filteredOptions[activeIndex]);
            } else if (showAddOption && activeIndex === filteredOptions.length) {
                handleAddClick();
            } else {
                setIsOpen(false);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div className="custom-select-container" ref={containerRef} style={{ width: '100%' }}>
            <input
                className="add-transporter-input"
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleInternalKeyDown}
                onFocus={() => {
                    setIsOpen(true);
                    setActiveIndex(0);
                }}
                onBlur={handleBlur}
                placeholder={placeholder || "Select or Type..."}
                style={{ width: '100%' }}
            />
            {isOpen && (filteredOptions.length > 0 || showAddOption) && (
                <div className="custom-select-dropdown" ref={dropdownRef}>
                    {filteredOptions.map((opt, index) => (
                        <div
                            key={opt}
                            className="custom-select-option"
                            style={{ backgroundColor: index === activeIndex ? '#f3f4f6' : 'transparent' }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleOptionClick(opt);
                            }}
                        >
                            {opt}
                        </div>
                    ))}
                    {showAddOption && (
                        <div
                            className="custom-select-add-option"
                            style={{ backgroundColor: activeIndex === filteredOptions.length ? '#f3f4f6' : 'transparent' }}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleAddClick();
                            }}
                        >
                            Add "{searchTerm}"
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function AddTransporterModal({ isOpen, onClose, isPage, onTransporterAdded, onSuccess, onKeyDown }) {
    const [formData, setFormData] = useState({
        transporterGst: '',
        transporterTradeName: '',
        transporterName: '',
        transporterReferenceName: '',
        transporterMobileNumber: '',
        transporterEmail: '',
        transporterCountry: '',
        transporterState: '',
        transporterCity: '',
        transporterPinCode: '',
        transporterStateCode: '',
        transporterPanNo: '',
        transporterType: 'Corporate',
        transporterAddress: ''
    });

    const gstRef = useRef(null);

    // Dynamic Lists - ideally fetch from API
    const [allCountries, setAllCountries] = useState([]);
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    const [allStates, setAllStates] = useState([]);
    const [transporterTypeList, setTransporterTypeList] = useState(['Corporate', 'Individual', 'Regular']);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                gstRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen && !isPage) return;
        const fetchTransporterTypes = async () => {
            const token = sessionStorage.getItem('token');
            try {
                const compId = sessionStorage.getItem('company_id');
                const response = await fetch(`${API_BASE_URL}/customer-type?type=transporter${compId ? `&companyId=${compId}` : ''}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    const list = data.customerTypes || (Array.isArray(data) ? data : []);
                    const names = list.map(t => t.customer_type_name || t.customerTypeName || t.name).filter(Boolean);
                    const merged = Array.from(new Set(['Corporate', 'Individual', 'Regular', ...names]));
                    setTransporterTypeList(merged);
                }
            } catch (error) {
                console.error("Error fetching transporter types:", error);
            }
        };
        fetchTransporterTypes();
    }, [isOpen, isPage]);

    useEffect(() => {
        const fetchCountries = async () => {
            const token = sessionStorage.getItem('token');
            try {
                const response = await fetch(`${API_BASE_URL}/country`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (response.ok) {
                    const data = await response.json();
                    const countries = Array.isArray(data) ? data : (data.countries || []);
                    setAllCountries(countries);
                    setCountryList(countries.map(c => c.country_name || c.countryName));
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
            }
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        const fetchStates = async () => {
            const country = allCountries.find(c => (c.country_name || c.countryName) === formData.transporterCountry);
            if (country) {
                const token = sessionStorage.getItem('token');
                try {
                    const res = await fetch(`${API_BASE_URL}/state?country_id=${country.id || country.countryId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const states = Array.isArray(data) ? data : (data.states || []);
                        setAllStates(states);
                        setStateList(states.map(s => s.state_name || s.stateName));
                    }
                } catch (error) {
                    console.error("Error fetching states:", error);
                }
            } else {
                setStateList([]);
            }
        };
        fetchStates();
    }, [formData.transporterCountry, allCountries]);

    useEffect(() => {
        const fetchCities = async () => {
            const state = allStates.find(s => (s.state_name || s.stateName) === formData.transporterState);
            if (state) {
                const token = sessionStorage.getItem('token');
                try {
                    const res = await fetch(`${API_BASE_URL}/city?state_id=${state.id || state.stateId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const data = await res.json();
                        const cities = Array.isArray(data) ? data : (data.cities || []);
                        setCityList(cities.map(c => c.city_name || c.cityName));
                    }
                } catch (error) {
                    console.error("Error fetching cities:", error);
                }
            } else {
                setCityList([]);
            }
        };
        fetchCities();
    }, [formData.transporterState, allStates]);

    const handleAddCity = async (newCityName) => {
        if (!newCityName) return;
        const state = allStates.find(s => (s.state_name || s.stateName) === formData.transporterState);
        if (!state) {
            toast.warning("please select state or country then add new city", {
                style: { backgroundColor: '#ffcc00', color: 'black' }
            });
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/city`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ city_name: newCityName, state_id: state.id || state.stateId })
            });

            if (res.ok) {
                const data = await res.json();
                const c = data.city || data;
                setCityList(prev => [...prev, c.city_name || c.cityName]);
                toast.success(`City "${newCityName}" added successfully`);
            } else {
                const err = await res.json();
                toast.error(err.message || "Failed to add city");
            }
        } catch (error) {
            console.error("Add City Error:", error);
            toast.error("Server error while adding city");
        }
    };

    const handleAddCountry = async (newCountryName) => {
        if (!newCountryName) return;
        try {
            const token = sessionStorage.getItem('token');
            const code = newCountryName.substring(0, 3).toUpperCase();

            const response = await fetch(`${API_BASE_URL}/country`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ country_name: newCountryName, country_code: code })
            });

            if (response.ok) {
                const data = await response.json();
                const c = data.country || data;
                setAllCountries(prev => [...prev, c]);
                setCountryList(prev => [...prev, c.country_name || c.countryName]);
                toast.success(`Country "${newCountryName}" added successfully`);
            } else {
                const err = await response.json();
                toast.error(err.message || "Failed to add country");
            }
        } catch (error) {
            console.error("Add Country Error:", error);
            toast.error("Server error while adding country");
        }
    };

    const handleAddState = async (newStateName) => {
        if (!newStateName) return;
        const country = allCountries.find(c => (c.country_name || c.countryName) === formData.transporterCountry);
        if (!country) {
            toast.warning("please select country then add new state");
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/state`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ state_name: newStateName, country_id: country.id || country.countryId })
            });
            if (res.ok) {
                const data = await res.json();
                const s = data.state || data;
                setAllStates(prev => [...prev, s]);
                setStateList(prev => [...prev, s.state_name || s.stateName]);
                toast.success(`State "${newStateName}" added successfully`);
            } else {
                const err = await res.json();
                toast.error(err.message || "Failed to add state");
            }
        } catch (error) {
            console.error("Add State Error:", error);
            toast.error("Server error while adding state");
        }
    };


    const handleAddTransporterType = async (newType) => {
        if (!newType) return;
        try {
            const token = sessionStorage.getItem('token');
            const compId = sessionStorage.getItem('company_id');
            const response = await fetch(`${API_BASE_URL}/customer-type`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ customerTypeName: newType, company_id: compId, type: 'transporter' })
            });
            if (response.ok) {
                const data = await response.json();
                const newTypeObj = data.customerType || data;
                const newTypeName = newTypeObj.customer_type_name || newTypeObj.customerTypeName || newTypeObj.name || newType;
                setTransporterTypeList(prev => Array.from(new Set([...prev, newTypeName])));
                setFormData(prev => ({ ...prev, transporterType: newTypeName }));
                toast.success(`Transporter Type "${newTypeName}" added successfully`);
            } else {
                toast.error("Failed to add transporter type");
            }
        } catch (error) {
            console.error("Add Transporter Type Error:", error);
            toast.error("Server error while adding transporter type");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            const form = e.target.form;
            const index = Array.prototype.indexOf.call(form, e.target);

            // Special case for Address textarea
            if (e.target.name === 'transporterAddress') {
                e.preventDefault();
                const submitBtn = Array.from(form.elements).find(el => el.type === 'submit' || el.classList.contains('add-transporter-btn-submit'));
                if (submitBtn) {
                    submitBtn.focus();
                }
                return;
            }

            if (index > -1 && index < form.elements.length - 1) {
                e.preventDefault();
                form.elements[index + 1]?.focus();
                if (typeof form.elements[index + 1].select === 'function') {
                    form.elements[index + 1].select();
                }
            }
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const form = e.target.form;
            const index = Array.prototype.indexOf.call(form, e.target);
            if (e.key === 'ArrowDown' && index < form.elements.length - 1) {
                e.preventDefault();
                form.elements[index + 1]?.focus();
            } else if (e.key === 'ArrowUp' && index > 0) {
                e.preventDefault();
                form.elements[index - 1]?.focus();
            }
        }
    };

    const [isVerifyingGst, setIsVerifyingGst] = useState(false);

    const handleGSTVerify = async (gstToVerify) => {
        const gst = (gstToVerify || '').trim().toUpperCase();
        if (!gst) {
            toast.warn('Please enter a GST number');
            return;
        }
        if (gst.length !== 15) {
            toast.warn('GST number must be exactly 15 characters');
            return;
        }

        setIsVerifyingGst(true);
        try {
            const token = sessionStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/gst/verify/${gst}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();
            if (res.ok && data.success && data.data) {
                const info = data.data;
                toast.success(`GST verified: ${info.legalName || info.tradeName}`);

                // Extract State Code from GSTIN (first 2 digits)
                const stateCode = gst.substring(0, 2);

                // Auto-determine Transporter Type based on Constitution of Business
                let matchedTransporterType = 'Corporate';
                const companyType = info.businessType || info.taxpayerType || '';
                if (companyType) {
                    matchedTransporterType = companyType;
                    const exists = transporterTypeList.some(t => t.toLowerCase() === companyType.toLowerCase());
                    if (!exists) {
                        try {
                            const token = sessionStorage.getItem('token');
                            const compId = sessionStorage.getItem('company_id');
                            const addRes = await fetch(`${API_BASE_URL}/customer-type`, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'Authorization': `Bearer ${token}`
                                },
                                body: JSON.stringify({ customerTypeName: companyType, company_id: compId, type: 'transporter' })
                            });
                            if (addRes.ok) {
                                const addData = await addRes.json();
                                const newTypeObj = addData.customerType || addData;
                                const newTypeName = newTypeObj.customer_type_name || newTypeObj.customerTypeName || newTypeObj.name || companyType;
                                setTransporterTypeList(prev => Array.from(new Set([...prev, newTypeName])));
                                matchedTransporterType = newTypeName;
                                toast.success(`Transporter Type "${newTypeName}" auto-saved successfully`);
                            }
                        } catch (e) {
                            console.error("Auto add transporter type error", e);
                        }
                    } else {
                        const matched = transporterTypeList.find(t => t.toLowerCase() === companyType.toLowerCase());
                        if (matched) matchedTransporterType = matched;
                    }
                } else {
                    const name = (info.tradeName || info.legalName || '').toLowerCase();
                    if (name) {
                        if (name.includes('ltd') || name.includes('limited') || name.includes('pvt') || name.includes('llp') || name.includes('corp') || name.includes('company') || name.includes('logistics') || name.includes('transport') || name.includes('express') || name.includes('road') || name.includes('carrier') || name.includes('services')) {
                            matchedTransporterType = 'Corporate';
                        } else if (name.includes('individual') || name.includes('proprietor') || name.includes('sole')) {
                            matchedTransporterType = 'Individual';
                        }
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    transporterGst: info.gstNumber || prev.transporterGst,
                    transporterTradeName: info.tradeName || info.legalName || prev.transporterTradeName,
                    transporterName: info.tradeName || info.legalName || prev.transporterName,
                    transporterAddress: info.address || prev.transporterAddress,
                    transporterState: info.state || prev.transporterState,
                    transporterCity: info.city || prev.transporterCity,
                    transporterPinCode: info.pincode || prev.transporterPinCode,
                    transporterPanNo: info.panNumber || prev.transporterPanNo,
                    transporterStateCode: stateCode || prev.transporterStateCode,
                    transporterCountry: 'India',
                    transporterType: matchedTransporterType || prev.transporterType
                }));
            } else {
                toast.error(data.message || 'GST verification failed');
                // Clear fields on failure
                setFormData(prev => ({
                    ...prev,
                    transporterTradeName: '',
                    transporterName: '',
                    transporterAddress: '',
                    transporterState: '',
                    transporterCity: '',
                    transporterPinCode: '',
                    transporterPanNo: '',
                    transporterStateCode: '',
                    transporterCountry: ''
                }));
            }
        } catch (err) {
            console.error('GST verify error:', err);
            toast.error('Error verifying GST details');
            // Clear fields on error
            setFormData(prev => ({
                ...prev,
                transporterTradeName: '',
                transporterName: '',
                transporterAddress: '',
                transporterState: '',
                transporterCity: '',
                transporterPinCode: '',
                transporterPanNo: '',
                transporterStateCode: '',
                transporterCountry: ''
            }));
        } finally {
            setIsVerifyingGst(false);
        }
    };

    const handleChange = (e) => {
                let { name, value } = e.target;
        if (name && name.toLowerCase().includes('gst')) {
            value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            if (value.length > 15) value = value.substring(0, 15);
        }

        if (name.toLowerCase().includes('adhar') || name.toLowerCase().includes('aadhar')) {
            let val = value.replace(/\D/g, '');
            if (val.length > 12) val = val.substring(0, 12);
            let formatted = val;
            if (val.length > 4) formatted = val.substring(0, 4) + ' ' + val.substring(4);
            if (val.length > 8) formatted = formatted.substring(0, 9) + ' ' + val.substring(8);
            setFormData(prev => ({ ...prev, [name]: formatted }));
            return;
        }
        if (
            name.toLowerCase().includes('pincode') || 
            name.toLowerCase().includes('pin_code') || 
            name.toLowerCase().includes('accountnumber') || 
            name.toLowerCase().includes('account_number') ||
            name.toLowerCase().includes('accountno') || 
            name.toLowerCase().includes('account_no')
        ) {
            const onlyDigits = value.replace(/\D/g, '');
            setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            return;
        }
        
        if (name.toLowerCase().includes('vehicle')) {
            setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
            return;
        }
        if (name.toLowerCase().includes('mobile')) {
            const onlyDigits = value.replace(/\D/g, '');
            if (onlyDigits.length <= 10) {
                setFormData(prev => ({ ...prev, [name]: onlyDigits }));
            }
            return;
        }

        if (name === 'transporterGst') {
            const upperVal = value.toUpperCase();
            setFormData(prev => ({
                ...prev,
                [name]: upperVal,
                // Clear auto-filled fields immediately on input change
                transporterTradeName: '',
                transporterName: '',
                transporterAddress: '',
                transporterState: '',
                transporterCity: '',
                transporterPinCode: '',
                transporterPanNo: '',
                transporterStateCode: '',
                transporterCountry: ''
            }));
            if (upperVal.length === 15) {
                handleGSTVerify(upperVal);
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };


    const handleSubmit = async (e) => {

    if(e) e.preventDefault();
    const conf = await Swal.fire({
      title: 'Save Data?',
      text: 'Are you sure you want to save this data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00334e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, Save',
      cancelButtonText: 'No, Keep Editing'
    });
    if(!conf.isConfirmed) return;
        e.preventDefault();



        try {
            const token = sessionStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/transporter`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                toast.success('Transporter added successfully!');
                const callback = onTransporterAdded || onSuccess;
                if (callback) callback(data.transporter);
                onClose();
            } else {
                toast.error(data.message || 'Failed to add transporter');
            }
        } catch (error) {
            console.error("Add Transporter Error:", error);
            toast.error("Server error");
        }
    };

    const handleReset = () => {
        setFormData({
            transporterGst: '',
            transporterTradeName: '',
            transporterName: '',
            transporterReferenceName: '',
            transporterMobileNumber: '',
            transporterEmail: '',
            transporterCountry: '',
            transporterState: '',
            transporterCity: '',
            transporterPinCode: '',
            transporterStateCode: '',
            transporterPanNo: '',
            transporterType: 'Corporate',
            transporterAddress: ''
        });
    };

    if (!isOpen && !isPage) return null;

    const formContent = (
        <div className={isPage ? "page-card" : "add-transporter-modal"} onClick={(e) => !isPage && e.stopPropagation()} style={isPage ? { maxWidth: '100%', margin: '0' } : {}}>
            {!isPage && (
                <div className="add-transporter-header">
                    <h2 className="add-transporter-title">Add Transporter</h2>
                    <button className="add-transporter-close" onClick={onClose}>✕</button>
                </div>
            )}
            {isPage && <div className="page-card__title">Add Transporter</div>}

            <div className={isPage ? "page-card__body" : ""}>
                <form className="add-transporter-form" onSubmit={handleSubmit} style={isPage ? { padding: '0' } : {}}>
                    {/* Row 1 */}
                    <div className="add-transporter-row">
                        <div className="add-transporter-field-with-btn add-transporter-field-span-2">
                            <label className="add-transporter-label">Transporter GST</label>
                            <div className="add-transporter-input-group">
                                <input
                                    ref={gstRef}
                                    className="add-transporter-input"
                                    placeholder="ENTER TRANSPORTER GST"
                                    name="transporterGst"
                                    value={formData.transporterGst}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                                <button
                                    type="button"
                                    className="add-transporter-btn-verify"
                                    onClick={() => handleGSTVerify(formData.transporterGst)}
                                    disabled={isVerifyingGst}
                                    onKeyDown={handleKeyDown}
                                >
                                    {isVerifyingGst ? 'Verifying...' : 'Verify'}
                                </button>
                            </div>
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Trade Name</label>
                            <input
                                className="add-transporter-input"
                                placeholder="Enter Transporter Trade Name"
                                name="transporterTradeName"
                                value={formData.transporterTradeName}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Name</label>
                            <input
                                className="add-transporter-input"
                                placeholder="Enter Transporter Name"
                                name="transporterName"
                                value={formData.transporterName}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Reference Name</label>
                            <input
                                className="add-transporter-input"
                                placeholder="Enter Reference Name"
                                name="transporterReferenceName"
                                value={formData.transporterReferenceName}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    {/* Row 2 - 5 fields */}
                    <div className="add-transporter-row">
                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Mobile Number</label>
                            <input
                                className="add-transporter-input"
                                placeholder="Enter Mobile Number"
                                name="transporterMobileNumber"
                                value={formData.transporterMobileNumber}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Email</label>
                            <input
                                className="add-transporter-input"
                                type="email"
                                placeholder="Enter Email"
                                name="transporterEmail"
                                value={formData.transporterEmail}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Country</label>
                            <CreatableSelect
                                value={formData.transporterCountry}
                                options={countryList}
                                onChange={(val) => setFormData(prev => ({ ...prev, transporterCountry: val }))}
                                onAddOption={(newVal) => {
                                    handleAddCountry(newVal);
                                    setFormData(prev => ({ ...prev, transporterCountry: newVal }));
                                }}
                                placeholder="Select or Type Country"
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter State</label>
                            <CreatableSelect
                                value={formData.transporterState}
                                options={stateList}
                                onChange={(val) => setFormData(prev => ({ ...prev, transporterState: val }))}
                                onAddOption={(newVal) => {
                                    handleAddState(newVal);
                                    setFormData(prev => ({ ...prev, transporterState: newVal }));
                                }}
                                placeholder="Select or Type State"
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter City</label>
                            <CreatableSelect
                                value={formData.transporterCity}
                                options={cityList}
                                onChange={(val) => setFormData(prev => ({ ...prev, transporterCity: val }))}
                                onAddOption={(newVal) => {
                                    handleAddCity(newVal);
                                    setFormData(prev => ({ ...prev, transporterCity: newVal }));
                                }}
                                placeholder="Select or Type City"
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    {/* Row 3 - 4 fields */}
                    <div className="add-transporter-row-4">
                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Pin Code</label>
                            <input
                                className="add-transporter-input"
                                placeholder="Enter Transporter Pin Code"
                                name="transporterPinCode"
                                value={formData.transporterPinCode}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter State Code</label>
                            <input
                                className="add-transporter-input"
                                placeholder="Enter Transporter State Code"
                                name="transporterStateCode"
                                value={formData.transporterStateCode}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter PAN No</label>
                            <input
                                className="add-transporter-input"
                                placeholder="Enter Transporter PAN No"
                                name="transporterPanNo"
                                value={formData.transporterPanNo}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                            />
                        </div>

                        <div className="add-transporter-field">
                            <label className="add-transporter-label">Transporter Type</label>
                            <CreatableSelect
                                value={formData.transporterType}
                                options={transporterTypeList}
                                onChange={(val) => setFormData(prev => ({ ...prev, transporterType: val }))}
                                onAddOption={(newVal) => handleAddTransporterType(newVal)}
                                placeholder="Select or Type Transporter Type"
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </div>

                    {/* Row 4 */}
                    <div className="add-transporter-field add-transporter-field-full">
                        <label className="add-transporter-label">Transporter Address</label>
                        <textarea
                            className="add-transporter-textarea"
                            placeholder="Enter Transporter Address"
                            name="transporterAddress"
                            value={formData.transporterAddress}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="add-transporter-actions">
                        {!isPage && <button type="button" className="add-transporter-btn-reset" onClick={handleReset}>Reset</button>}
                        {isPage && <button type="button" className="add-transporter-btn-reset" onClick={onClose} style={{ background: '#fff', border: '1px solid #ddd', color: '#333' }}>Cancel</button>}
                        <button type="submit" className="add-transporter-btn-submit">Submit</button>
                    </div>
                </form>
            </div>
        </div>
    );

    if (!isOpen && !isPage) return null;

    if (isPage) return formContent;

    return (
        <div className="add-transporter-modal-overlay" onClick={onClose}>
            {formContent}
        </div>
    );
}

export default AddTransporterModal;
